import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Avatar, Box, Button, Card, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, IconButton, InputAdornment, MenuItem,
  Pagination, Select, Skeleton, Stack, TextField, Tooltip, Typography
} from '@mui/material';
import Icon from '../../../components/icon';
import apiUrl from '../../../configs/constant';
import api from '../../../utils/axiosConfig';
import './work-order.scss';

interface WorkOrder {
  id: number | string; kode: string; dokumen: string; tgl: string;
  product?: { kode?: string; nama?: string; gambar?: string };
  qty: number; satuan: string; qty_label?: string; jenis?: string;
  deadline?: string; is_terlambat: boolean; sisa_hari?: number; notes?: string;
  status?: { key?: string; label?: string; color?: string };
  tgl_aktif?: string | null; tgl_selesai?: string | null; is_aktif: boolean | number;
}

interface TicketMaster {
  id: number | string; kode?: string; qty: number; qty_doc: number; jumlah_tiket: number;
  total_qty_tiket: number; satuan?: string; notes?: string; nama_product?: string;
  kode_product?: string; nama_barang?: string; kode_barang?: string;
}

interface Ticket {
  id: number | string; wo_order_id: number | string; nomor_tiket: number;
  qty: number; qty_doc: number; notes?: string | null; created_at?: string;
  qr_value?: string; scan_url?: string; print_url?: string;
}

interface TicketDetailResponse {
  master: Record<string, any>; jumlah_tiket: number; total_qty_tiket: number; detail: Ticket[];
}

interface WorkOrderProcess {
  wo_plan_id: number | string; urutan: number; nama_step?: string; keterangan?: string;
  status?: string; qty_target?: number; qty_hasil?: number;
}

interface Summary { total: number; draft: number; siap: number; proses: number; selesai: number; terlambat: number }

const emptySummary: Summary = { total: 0, draft: 0, siap: 0, proses: 0, selesai: 0, terlambat: 0 };
const pad = (value: number) => String(value).padStart(2, '0');
const dateValue = (date: Date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
const defaultPeriod = () => {
  const now = new Date();
  return {
    awal: dateValue(new Date(now.getFullYear(), now.getMonth(), 1)),
    akhir: dateValue(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
  };
};
const formatDate = (value?: string | null) => value
  ? new Intl.DateTimeFormat('id-ID', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(`${value.slice(0, 10)}T00:00:00`))
  : '—';
const formatQty = (value?: number | string) => Number(value || 0).toLocaleString('id-ID', { maximumFractionDigits: 6 });
const errorMessage = (error: any, fallback = 'Terjadi kesalahan. Silakan coba lagi.') => {
  const validationErrors = error?.response?.data?.errors;
  const firstValidationError = validationErrors && Object.values(validationErrors).flat()[0];
  return String(firstValidationError || error?.response?.data?.message || error?.message || fallback);
};
const getStatus = (row: WorkOrder) => {
  if (row.tgl_selesai) return { key: 'selesai', label: 'Selesai', color: 'success' as const };
  if (row.tgl_aktif) return { key: 'proses', label: 'Sedang diproses', color: 'primary' as const };
  if (Boolean(row.is_aktif)) return { key: 'siap', label: 'Siap diproses', color: 'info' as const };
  return { key: 'draft', label: 'Draft', color: 'default' as const };
};
const summaryItems: Array<{ key: keyof Summary; label: string; icon: string; tone: string }> = [
  { key: 'total', label: 'Total WO', icon: 'mdi:clipboard-text-outline', tone: 'blue' },
  { key: 'draft', label: 'Draft', icon: 'mdi:file-edit-outline', tone: 'gray' },
  { key: 'siap', label: 'Siap', icon: 'mdi:check-decagram-outline', tone: 'cyan' },
  { key: 'proses', label: 'Diproses', icon: 'mdi:cog-sync-outline', tone: 'purple' },
  { key: 'selesai', label: 'Selesai', icon: 'mdi:check-circle-outline', tone: 'green' },
  { key: 'terlambat', label: 'Terlambat', icon: 'mdi:clock-alert-outline', tone: 'red' },
];

export default function KddWorkOrderIndex() {
  const defaults = useMemo(defaultPeriod, []);
  const [awal, setAwal] = useState(defaults.awal);
  const [akhir, setAkhir] = useState(defaults.akhir);
  const [period, setPeriod] = useState(defaults);
  const [rows, setRows] = useState<WorkOrder[]>([]);
  const [ticketMasters, setTicketMasters] = useState<Record<string, TicketMaster>>({});
  const [summary, setSummary] = useState<Summary>(emptySummary);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState<WorkOrder | null>(null);
  const [ticketDetail, setTicketDetail] = useState<TicketDetailResponse | null>(null);
  const [ticketDialogOpen, setTicketDialogOpen] = useState(false);
  const [ticketLoading, setTicketLoading] = useState(false);
  const [ticketError, setTicketError] = useState('');
  const [ticketNotice, setTicketNotice] = useState('');
  const [addingTicket, setAddingTicket] = useState(false);
  const [editingTicket, setEditingTicket] = useState<Ticket | null>(null);
  const [deletingTicket, setDeletingTicket] = useState<Ticket | null>(null);
  const [addQty, setAddQty] = useState('');
  const [addNotes, setAddNotes] = useState('');
  const [editQty, setEditQty] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [mutationError, setMutationError] = useState('');
  const [mutating, setMutating] = useState(false);
  const [printOrder, setPrintOrder] = useState<WorkOrder | null>(null);
  const [printProcesses, setPrintProcesses] = useState<WorkOrderProcess[]>([]);
  const [selectedPrintPlanId, setSelectedPrintPlanId] = useState('');
  const [printLoading, setPrintLoading] = useState(false);
  const [printError, setPrintError] = useState('');
  const perPage = 10;

  const loadData = useCallback(async (dates: { awal: string; akhir: string }, signal?: AbortSignal) => {
    setLoading(true);
    setError('');
    try {
      const [workOrderResponse, ticketResponse] = await Promise.all([
        api.get('/production/work-order-kedinding/list', { params: dates, signal }),
        api.get('/production/work-order/ticket/master', { params: dates, signal }),
      ]);
      if (workOrderResponse.data?.success === false) throw new Error(workOrderResponse.data.message || 'Data Work Order gagal dimuat.');
      if (ticketResponse.data?.success === false) throw new Error(ticketResponse.data.message || 'Data tiket gagal dimuat.');

      const workOrders = Array.isArray(workOrderResponse.data?.data) ? workOrderResponse.data.data : [];
      const masters = Array.isArray(ticketResponse.data?.data) ? ticketResponse.data.data : [];
      setRows(workOrders);
      setSummary({ ...emptySummary, ...(workOrderResponse.data?.summary || {}) });
      setTicketMasters(masters.reduce((result: Record<string, TicketMaster>, item: TicketMaster) => {
        result[String(item.id)] = item;
        return result;
      }, {}));
    } catch (requestError: any) {
      if (requestError?.code !== 'ERR_CANCELED') setError(errorMessage(requestError, 'Data Work Order gagal dimuat.'));
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    loadData(period, controller.signal);
    return () => controller.abort();
  }, [loadData, period]);

  const loadTicketDetail = useCallback(async (woOrderId: number | string) => {
    setTicketLoading(true);
    setTicketError('');
    try {
      const { data } = await api.get(`/production/work-order/ticket/detail/${woOrderId}`);
      if (data?.success === false) throw new Error(data.message || 'Detail tiket gagal dimuat.');
      setTicketDetail(data?.data || null);
      return data?.data as TicketDetailResponse;
    } catch (requestError) {
      setTicketError(errorMessage(requestError, 'Detail tiket gagal dimuat.'));
      return null;
    } finally {
      setTicketLoading(false);
    }
  }, []);

  const openTickets = (row: WorkOrder) => {
    setSelectedOrder(row);
    setTicketDetail(null);
    setTicketError('');
    setTicketNotice('');
    setTicketDialogOpen(true);
    loadTicketDetail(row.id);
  };

  const applyPeriod = () => {
    if (!awal || !akhir) return setError('Tanggal awal dan akhir wajib diisi.');
    if (awal > akhir) return setError('Tanggal awal tidak boleh melebihi tanggal akhir.');
    setPeriod({ awal, akhir });
  };

  const filtered = useMemo(() => rows.filter(row => {
    const keyword = search.toLowerCase();
    const status = getStatus(row).key;
    const textMatch = `${row.kode} ${row.product?.kode || ''} ${row.product?.nama || ''} ${row.notes || ''}`.toLowerCase().includes(keyword);
    const statusMatch = statusFilter === 'all' || (statusFilter === 'terlambat' ? row.is_terlambat : status === statusFilter);
    return textMatch && statusMatch;
  }), [rows, search, statusFilter]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / perPage));
  const visibleRows = filtered.slice((page - 1) * perPage, page * perPage);

  useEffect(() => setPage(1), [search, statusFilter, period]);
  useEffect(() => { if (page > pageCount) setPage(pageCount); }, [page, pageCount]);

  const refreshTicketSummary = (woOrderId: number | string, detail: TicketDetailResponse) => {
    setTicketMasters(current => ({
      ...current,
      [String(woOrderId)]: {
        ...(current[String(woOrderId)] || { id: woOrderId }),
        id: woOrderId,
        qty: detail.total_qty_tiket,
        qty_doc: detail.total_qty_tiket,
        jumlah_tiket: detail.jumlah_tiket,
        total_qty_tiket: detail.total_qty_tiket,
      } as TicketMaster,
    }));
    setRows(current => current.map(row => row.id === woOrderId || String(row.id) === String(woOrderId)
      ? { ...row, qty: detail.total_qty_tiket, qty_label: `${formatQty(detail.total_qty_tiket)} ${row.satuan || ''}`.trim() }
      : row));
  };

  const startEdit = (ticket: Ticket) => {
    setEditingTicket(ticket);
    setEditQty(String(ticket.qty));
    setEditNotes(ticket.notes || '');
    setTicketError('');
    setMutationError('');
  };

  const startAdd = () => {
    setAddQty('');
    setAddNotes('');
    setMutationError('');
    setAddingTicket(true);
  };

  const submitAdd = async (event: FormEvent) => {
    event.preventDefault();
    const numericQty = Number(addQty);
    if (!selectedOrder || !Number.isFinite(numericQty) || numericQty <= 0) return;
    setMutating(true);
    setMutationError('');
    try {
      const { data } = await api.post(`/production/work-order/ticket/add/${selectedOrder.id}`, {
        qty: numericQty,
        notes: addNotes.trim() || null,
      });
      if (data?.success === false) throw new Error(data.message || 'Tiket gagal ditambahkan.');
      setAddingTicket(false);
      setTicketNotice(data?.message || 'Tiket berhasil ditambahkan.');
      const detail = await loadTicketDetail(selectedOrder.id);
      if (detail) refreshTicketSummary(selectedOrder.id, detail);
    } catch (requestError) {
      setMutationError(errorMessage(requestError, 'Tiket gagal ditambahkan.'));
    } finally {
      setMutating(false);
    }
  };

  const ticketPrintUrl = (ticket: Ticket) => `${apiUrl.baseUrl}/production/work-order/ticket/cetak/${encodeURIComponent(String(ticket.id))}`;
  const workOrderTicketsPrintUrl = (woOrderId: number | string, woPlanId: number | string) => {
    const params = new URLSearchParams({ wo_plan_id: String(woPlanId) });
    return `${apiUrl.baseUrl}/production/work-order/ticket/cetak-wo/${encodeURIComponent(String(woOrderId))}?${params.toString()}`;
  };

  const openPrintDialog = async (row: WorkOrder) => {
    setPrintOrder(row);
    setPrintProcesses([]);
    setSelectedPrintPlanId('');
    setPrintError('');
    setPrintLoading(true);
    try {
      const { data } = await api.get(`/production/work-order-kedinding/list-proses-plan/${row.id}`);
      if (data?.success === false) throw new Error(data.message || 'Daftar proses gagal dimuat.');
      const processes = data?.data?.steps ?? data?.data;
      if (!Array.isArray(processes) || processes.length === 0) throw new Error('Work Order ini belum memiliki proses yang dapat dicetak.');
      setPrintProcesses(processes);
    } catch (requestError) {
      setPrintError(errorMessage(requestError, 'Daftar proses gagal dimuat.'));
    } finally {
      setPrintLoading(false);
    }
  };

  const closePrintDialog = () => {
    if (printLoading) return;
    setPrintOrder(null);
    setPrintProcesses([]);
    setSelectedPrintPlanId('');
    setPrintError('');
  };

  const submitEdit = async (event: FormEvent) => {
    event.preventDefault();
    const numericQty = Number(editQty);
    if (!editingTicket || !Number.isFinite(numericQty) || numericQty <= 0) return;
    setMutating(true);
    setMutationError('');
    try {
      const { data } = await api.post(`/production/work-order/ticket/edit/${editingTicket.id}`, {
        qty: numericQty,
        notes: editNotes.trim() || null,
      });
      if (data?.success === false) throw new Error(data.message || 'Tiket gagal diubah.');
      const woOrderId = editingTicket.wo_order_id || selectedOrder?.id;
      setEditingTicket(null);
      setTicketNotice(data?.message || 'Tiket berhasil diubah.');
      if (woOrderId != null) {
        const detail = await loadTicketDetail(woOrderId);
        if (detail) refreshTicketSummary(woOrderId, detail);
      }
    } catch (requestError) {
      setMutationError(errorMessage(requestError, 'Tiket gagal diubah.'));
    } finally {
      setMutating(false);
    }
  };

  const confirmDelete = async () => {
    if (!deletingTicket) return;
    setMutating(true);
    setMutationError('');
    try {
      const { data } = await api.delete(`/production/work-order/ticket/delete/${deletingTicket.id}`);
      if (data?.success === false) throw new Error(data.message || 'Tiket gagal dihapus.');
      const woOrderId = deletingTicket.wo_order_id || selectedOrder?.id;
      setDeletingTicket(null);
      setTicketNotice(data?.message || 'Tiket berhasil dihapus.');
      if (woOrderId != null) {
        const detail = await loadTicketDetail(woOrderId);
        if (detail) refreshTicketSummary(woOrderId, detail);
      }
    } catch (requestError) {
      setMutationError(errorMessage(requestError, 'Tiket gagal dihapus.'));
    } finally {
      setMutating(false);
    }
  };

  const closeTicketDialog = () => {
    if (mutating) return;
    setTicketDialogOpen(false);
    setAddingTicket(false);
    setEditingTicket(null);
    setDeletingTicket(null);
  };

  return <div className="kdd-wo-page">
    <Box className="kdd-wo-heading">
      <Box>
        <Typography variant="h4" fontWeight={750}>Work Order Kedinding</Typography>
        <Typography color="text.secondary">Pantau kesiapan, progres produksi, dan tiket Work Order dalam satu tampilan.</Typography>
      </Box>
      <Chip icon={<Icon icon="mdi:calendar-month-outline" />} label={`${formatDate(period.awal)} — ${formatDate(period.akhir)}`} variant="outlined" color="primary" />
    </Box>

    <Card className="kdd-filter-card">
      <TextField size="small" type="date" label="Tanggal awal" value={awal} onChange={event => setAwal(event.target.value)} InputLabelProps={{ shrink: true }} />
      <Icon icon="mdi:arrow-right" className="date-arrow" />
      <TextField size="small" type="date" label="Tanggal akhir" value={akhir} onChange={event => setAkhir(event.target.value)} InputLabelProps={{ shrink: true }} />
      <Button variant="contained" onClick={applyPeriod} disabled={loading} startIcon={<Icon icon="mdi:filter-outline" />}>Tampilkan</Button>
    </Card>

    {error && <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={() => loadData(period)}>Coba lagi</Button>}>{error}</Alert>}

    <Box className="summary-grid">
      {summaryItems.map(item => <Card key={item.key} className={`summary-card tone-${item.tone}`} onClick={() => setStatusFilter(item.key === 'total' ? 'all' : item.key)}>
        <Box className="summary-icon"><Icon icon={item.icon} /></Box>
        <Box>
          {loading ? <Skeleton width={45} height={35} /> : <Typography variant="h5" fontWeight={800}>{summary[item.key]}</Typography>}
          <Typography variant="body2" color="text.secondary">{item.label}</Typography>
        </Box>
      </Card>)}
    </Box>

    <Card className="kdd-table-card">
      <Box className="kdd-toolbar">
        <TextField size="small" placeholder="Cari WO atau produk..." value={search} onChange={event => setSearch(event.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><Icon icon="mdi:magnify" /></InputAdornment> }} />
        <Select size="small" value={statusFilter} onChange={event => setStatusFilter(event.target.value)}>
          <MenuItem value="all">Semua status</MenuItem><MenuItem value="draft">Draft</MenuItem><MenuItem value="siap">Siap</MenuItem>
          <MenuItem value="proses">Diproses</MenuItem><MenuItem value="selesai">Selesai</MenuItem><MenuItem value="terlambat">Terlambat</MenuItem>
        </Select>
        <Typography variant="body2" color="text.secondary" ml="auto">{filtered.length} work order</Typography>
      </Box>
      <Box className="kdd-table-wrap">
        <table className="kdd-wo-table">
          <thead><tr><th>Work order</th><th>Produk</th><th>Jumlah</th><th>Tiket</th><th>Timeline</th><th>Status</th></tr></thead>
          <tbody>
            {loading ? Array.from({ length: 6 }).map((_, row) => <tr key={row}>{Array.from({ length: 6 }).map((__, cell) => <td key={cell}><Skeleton height={42} /></td>)}</tr>) : visibleRows.map(row => {
              const status = getStatus(row);
              const ticketMaster = ticketMasters[String(row.id)];
              return <tr key={row.id} className={row.is_terlambat ? 'late-row' : ''}>
                <td><Stack spacing={0.4}><Typography fontWeight={750}>{row.kode}</Typography><Typography variant="caption" color="text.secondary">{formatDate(row.tgl)} • {row.jenis || row.dokumen}</Typography>{row.notes && <Stack direction="row" spacing={0.5} className="wo-note"><Icon icon="mdi:note-text-outline" /><Typography variant="caption" title={row.notes}>{row.notes}</Typography></Stack>}</Stack></td>
                <td><Stack direction="row" spacing={1.3} alignItems="center"><Avatar variant="rounded" src={row.product?.gambar} className="product-image"><Icon icon="mdi:package-variant-closed" /></Avatar><Box><Typography fontWeight={650}>{row.product?.nama || 'Produk tidak tersedia'}</Typography><Typography variant="caption" color="text.secondary">{row.product?.kode || '—'}</Typography></Box></Stack></td>
                <td><Typography fontWeight={750}>{row.qty_label || `${formatQty(row.qty)} ${row.satuan}`}</Typography><Typography variant="caption" color="text.secondary">Target produksi</Typography></td>
                <td><Stack alignItems="flex-start" spacing={0.7}><Chip size="small" color="primary" variant="outlined" icon={<Icon icon="mdi:ticket-confirmation-outline" />} label={`${ticketMaster?.jumlah_tiket || 0} tiket`} /><Typography variant="caption" color="text.secondary">Total {formatQty(ticketMaster?.total_qty_tiket)} {ticketMaster?.satuan || row.satuan || ''}</Typography><Box className="ticket-row-actions"><Button size="small" onClick={() => openTickets(row)} startIcon={<Icon icon="mdi:pencil-outline" />}>Kelola</Button><Button onClick={() => openPrintDialog(row)} size="small" variant="outlined" startIcon={<Icon icon="mdi:printer-outline" />}>Cetak tiket</Button></Box></Stack></td>
                <td><Stack spacing={0.5}><Box display="flex" gap={0.7} alignItems="center"><Icon icon="mdi:calendar-flag" /><Typography variant="body2">{formatDate(row.deadline)}</Typography></Box>{row.is_terlambat ? <Chip size="small" color="error" icon={<Icon icon="mdi:alert-circle-outline" />} label="Melewati deadline" /> : row.sisa_hari != null && !row.tgl_selesai ? <Typography variant="caption" color={row.sisa_hari <= 3 ? 'warning.main' : 'text.secondary'}>{row.sisa_hari >= 0 ? `${row.sisa_hari} hari tersisa` : `${Math.abs(row.sisa_hari)} hari terlambat`}</Typography> : null}</Stack></td>
                <td><Stack alignItems="flex-start" spacing={0.7}><Chip size="small" color={status.color} label={status.label} /><Typography variant="caption" color="text.secondary">{row.tgl_selesai ? `Selesai ${formatDate(row.tgl_selesai)}` : row.tgl_aktif ? `Aktif ${formatDate(row.tgl_aktif)}` : Boolean(row.is_aktif) ? 'Menunggu proses' : 'Belum diaktifkan'}</Typography></Stack></td>
              </tr>;
            })}
          </tbody>
        </table>
        {!loading && !error && visibleRows.length === 0 && <Box className="kdd-empty"><Icon icon="mdi:clipboard-text-search-outline" fontSize={52} /><Typography variant="h6">Work order tidak ditemukan</Typography><Typography color="text.secondary">Coba ubah rentang tanggal, status, atau pencarian.</Typography></Box>}
      </Box>
      {!loading && filtered.length > perPage && <Box display="flex" justifyContent="flex-end" p={2}><Pagination count={pageCount} page={page} onChange={(_, value) => setPage(value)} color="primary" /></Box>}
    </Card>

    <Dialog open={ticketDialogOpen} onClose={closeTicketDialog} fullWidth maxWidth="md" PaperProps={{ className: 'ticket-dialog' }}>
      <DialogTitle className="ticket-dialog-title">
        <Box className="ticket-title-icon"><Icon icon="mdi:ticket-confirmation-outline" /></Box>
        <Box flex={1}><Typography variant="h5" fontWeight={800}>Tiket Work Order</Typography><Typography variant="body2" color="text.secondary">{selectedOrder?.kode} • {selectedOrder?.product?.nama || 'Produk tidak tersedia'}</Typography></Box>
        <Button variant="contained" onClick={startAdd} disabled={ticketLoading || mutating} startIcon={<Icon icon="mdi:ticket-confirmation-outline" />}>Tambah tiket</Button>
        <IconButton onClick={closeTicketDialog} disabled={mutating}><Icon icon="mdi:close" /></IconButton>
      </DialogTitle>
      <DialogContent dividers className="ticket-dialog-content">
        {ticketError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setTicketError('')}>{ticketError}</Alert>}
        {ticketNotice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setTicketNotice('')}>{ticketNotice}</Alert>}
        {ticketLoading && !ticketDetail ? <Stack spacing={1.5}>{Array.from({ length: 4 }).map((_, index) => <Skeleton key={index} variant="rounded" height={70} />)}</Stack> : ticketDetail && <>
          <Box className="ticket-summary">
            <Box><Typography variant="caption">JUMLAH TIKET</Typography><Typography variant="h5" fontWeight={850}>{ticketDetail.jumlah_tiket}</Typography></Box>
            <Divider orientation="vertical" flexItem />
            <Box><Typography variant="caption">TOTAL QTY</Typography><Typography variant="h5" fontWeight={850}>{formatQty(ticketDetail.total_qty_tiket)} <small>{selectedOrder?.satuan || ''}</small></Typography></Box>
            {/* <Divider orientation="vertical" flexItem /> */}
            {/* <Box><Typography variant="caption">BATAS PER TIKET</Typography><Typography variant="h5" fontWeight={850}>50 <small>{selectedOrder?.satuan || ''}</small></Typography></Box> */}
          </Box>
          <Alert severity="info" icon={<Icon icon="mdi:qrcode-scan" />} sx={{ mb: 2 }}>Cetak menggunakan format thermal 58 mm. QR dan barcode memuat kode tiket stabil yang dapat dipindai untuk proses penyelesaian maupun pencairan.</Alert>
          <Box className="ticket-table-wrap">
            <table className="ticket-table">
              <thead><tr><th>No.</th><th>ID tiket</th><th>Qty</th><th>Catatan</th><th>Dibuat</th><th>Aksi</th></tr></thead>
              <tbody>{ticketDetail.detail.map(ticket => <tr key={ticket.id}>
                <td><span className="ticket-number">{ticket.nomor_tiket}</span></td>
                <td><Typography variant="body2" fontWeight={650}>{ticket.id}</Typography><Typography className="ticket-qr-value" variant="caption" color="text.secondary">{ticket.qr_value || `MLF-WO-TICKET:${ticket.id}`}</Typography></td>
                <td><Typography fontWeight={800}>{formatQty(ticket.qty)} <small>{selectedOrder?.satuan || ''}</small></Typography></td>
                <td><Typography variant="body2" color={ticket.notes ? 'text.primary' : 'text.secondary'}>{ticket.notes || '—'}</Typography></td>
                <td><Typography variant="body2" color="text.secondary">{formatDate(ticket.created_at)}</Typography></td>
                <td><Stack direction="row" spacing={0.5}><Tooltip title="Cetak tiket thermal 58 mm"><IconButton component="a" href={ticketPrintUrl(ticket)} target="_blank" rel="noopener noreferrer" size="small" color="primary"><Icon icon="mdi:printer-outline" /></IconButton></Tooltip><Tooltip title="Edit tiket"><IconButton size="small" color="primary" onClick={() => startEdit(ticket)}><Icon icon="mdi:pencil-outline" /></IconButton></Tooltip><Tooltip title="Hapus tiket"><IconButton size="small" color="error" onClick={() => { setDeletingTicket(ticket); setTicketError(''); setMutationError(''); }}><Icon icon="mdi:delete-outline" /></IconButton></Tooltip></Stack></td>
              </tr>)}</tbody>
            </table>
            {!ticketDetail.detail.length && <Box className="ticket-empty"><Icon icon="mdi:ticket-outline" fontSize={48} /><Typography variant="h6">Belum ada tiket</Typography><Typography variant="body2" color="text.secondary">Semua tiket pada Work Order ini telah dihapus.</Typography></Box>}
          </Box>
        </>}
      </DialogContent>
      <DialogActions><Button variant="contained" onClick={closeTicketDialog} disabled={mutating}>Tutup</Button></DialogActions>
    </Dialog>

    <Dialog open={Boolean(printOrder)} onClose={closePrintDialog} fullWidth maxWidth="xs">
      <DialogTitle>Cetak Tiket per Proses<Typography variant="body2" color="text.secondary">{printOrder?.kode} • Pilih proses yang akan dicetak.</Typography></DialogTitle>
      <DialogContent dividers><Stack spacing={2} pt={0.5}>
        {printError && <Alert severity="error">{printError}</Alert>}
        {printLoading ? <Stack direction="row" spacing={1.5} alignItems="center"><CircularProgress size={22} /><Typography color="text.secondary">Memuat daftar proses...</Typography></Stack> : !printError && <TextField select required autoFocus fullWidth label="Proses" value={selectedPrintPlanId} onChange={event => setSelectedPrintPlanId(event.target.value)} helperText="Tiket yang dicetak hanya untuk proses terpilih.">
          <MenuItem value="" disabled>Pilih proses</MenuItem>
          {printProcesses.map(process => <MenuItem key={process.wo_plan_id} value={String(process.wo_plan_id)}>Proses {process.urutan} — {process.nama_step || process.keterangan || `Proses ${process.urutan}`}</MenuItem>)}
        </TextField>}
      </Stack></DialogContent>
      <DialogActions><Button onClick={closePrintDialog} disabled={printLoading}>Tutup</Button><Button component="a" href={printOrder && selectedPrintPlanId ? workOrderTicketsPrintUrl(printOrder.id, selectedPrintPlanId) : undefined} target="_blank" rel="noopener noreferrer" variant="contained" disabled={printLoading || Boolean(printError) || !selectedPrintPlanId} startIcon={<Icon icon="mdi:printer-outline" />} sx={{ color: '#fff', '&:hover': { color: '#fff' }, '&:visited': { color: '#fff' } }}>Buka halaman cetak</Button></DialogActions>
    </Dialog>

    <Dialog open={addingTicket} onClose={() => { if (!mutating) { setAddingTicket(false); setMutationError(''); } }} fullWidth maxWidth="xs">
      <form onSubmit={submitAdd}>
        <DialogTitle>Tambah Tiket<Typography variant="body2" color="text.secondary">{selectedOrder?.kode} • Qty harus lebih dari 0.</Typography></DialogTitle>
        <DialogContent dividers><Stack spacing={2} pt={0.5}>
          {mutationError && <Alert severity="error">{mutationError}</Alert>}
          {/* <TextField required autoFocus type="number" label="Qty tiket" value={addQty} onChange={event => setAddQty(event.target.value)} inputProps={{ min: 0.000001, max: 50, step: 'any' }} error={addQty !== '' && (Number(addQty) <= 0 || Number(addQty) > 50)} helperText={addQty !== '' && Number(addQty) > 50 ? 'Qty maksimal 50 per tiket.' : 'Masukkan qty lebih dari 0.'} /> */}
          <TextField required autoFocus type="number" label="Qty tiket" value={addQty} onChange={event => setAddQty(event.target.value)} inputProps={{ min: 0.000001, max: 9999999, step: 'any' }}  />
          <TextField multiline minRows={3} label="Catatan" value={addNotes} onChange={event => setAddNotes(event.target.value)} placeholder="Catatan tiket (opsional)" />
        </Stack></DialogContent>
        <DialogActions><Button onClick={() => { setAddingTicket(false); setMutationError(''); }} disabled={mutating}>Batal</Button><Button type="submit" variant="contained" disabled={mutating || !addQty} startIcon={mutating ? <CircularProgress size={18} color="inherit" /> : <Icon icon="mdi:plus" />}>{mutating ? 'Menambahkan...' : 'Tambah tiket'}</Button></DialogActions>
      </form>
    </Dialog>

    <Dialog open={Boolean(editingTicket)} onClose={() => { if (!mutating) { setEditingTicket(null); setMutationError(''); } }} fullWidth maxWidth="xs">
      <form onSubmit={submitEdit}>
        <DialogTitle>Edit Tiket #{editingTicket?.nomor_tiket}<Typography variant="body2" color="text.secondary">Qty harus lebih dari 0.</Typography></DialogTitle>
        <DialogContent dividers><Stack spacing={2} pt={0.5}>
          {mutationError && <Alert severity="error">{mutationError}</Alert>}
          {/* <TextField required autoFocus type="number" label="Qty tiket" value={editQty} onChange={event => setEditQty(event.target.value)} inputProps={{ min: 0.000001, max: 999999, step: 'any' }} error={editQty !== '' && (Number(editQty) <= 0 || Number(editQty) > 50)} helperText={editQty !== '' && Number(editQty) > 50 ? 'Qty maksimal 50 per tiket.' : 'Masukkan qty lebih dari 0.'} /> */}
          <TextField required autoFocus type="number" label="Qty tiket" value={editQty} onChange={event => setEditQty(event.target.value)} inputProps={{ min: 0.000001, max: 999999, step: 'any' }} />
          <TextField multiline minRows={3} label="Catatan" value={editNotes} onChange={event => setEditNotes(event.target.value)} placeholder="Catatan tiket (opsional)" />
        </Stack></DialogContent>
        <DialogActions><Button onClick={() => { setEditingTicket(null); setMutationError(''); }} disabled={mutating}>Batal</Button><Button type="submit" variant="contained" disabled={mutating || !editQty} startIcon={mutating ? <CircularProgress size={18} color="inherit" /> : <Icon icon="mdi:content-save-outline" />}>{mutating ? 'Menyimpan...' : 'Simpan'}</Button></DialogActions>
      </form>
    </Dialog>

    <Dialog open={Boolean(deletingTicket)} onClose={() => { if (!mutating) { setDeletingTicket(null); setMutationError(''); } }} fullWidth maxWidth="xs">
      <DialogTitle>Hapus Tiket?</DialogTitle>
      <DialogContent dividers><Stack spacing={2}>{mutationError && <Alert severity="error">{mutationError}</Alert>}<Alert severity="warning">Tiket #{deletingTicket?.nomor_tiket} dengan qty {formatQty(deletingTicket?.qty)} akan dihapus. Total qty Work Order akan dihitung ulang otomatis.</Alert></Stack></DialogContent>
      <DialogActions><Button onClick={() => { setDeletingTicket(null); setMutationError(''); }} disabled={mutating}>Batal</Button><Button color="error" variant="contained" onClick={confirmDelete} disabled={mutating} startIcon={mutating ? <CircularProgress size={18} color="inherit" /> : <Icon icon="mdi:delete-outline" />}>{mutating ? 'Menghapus...' : 'Hapus tiket'}</Button></DialogActions>
    </Dialog>
  </div>;
}
